import { supabase } from '../supabase';
import { toStateCode } from '../constants/usStates';

// Shared cascade matcher — resolves an extracted document to a property/parcel by every identifier it carries, in
// strength order. Lifted VERBATIM from DocumentScanner's live tax-bill path so the scanner and Document Intake's
// pre-identify run the SAME matcher (one implementation, two consumers). PURE: returns the match-result shape and
// never touches React state — the caller decides what to do with it (scanner → setAutoMatch; Intake → prefill form).

// Parcel/account numbers: strip every separator + leading zeros so "38 00 14 2 001 001.023" compares equal
// regardless of how the DB stored it.
export const normNum = (s) => String(s || '').toUpperCase().replace(/[^0-9A-Z]/g, '').replace(/^0+/, '');

// Pull a parcel/assessor number out of legal-description text when the doc has no dedicated field.
export function parcelNumFromLegal(legal) {
  const s = String(legal || '');
  let m = s.match(/parcel\s*(?:no\.?|#|number)?\s*[:.]?\s*([0-9][0-9 .-]{5,}[0-9])/i);
  if (m) return m[1].trim();
  m = s.match(/\b([0-9]{2}(?:[ .-][0-9]+){3,})\b/);   // grouped numeric id, e.g. 38 00 14 2 001 001.023
  return m ? m[1].trim() : '';
}

// Pull number-like runs out of free text (grouped digits w/ separators): "PCN 00-43-43-30-00-000-5110" → the run.
const tokenizeNums = (s) => String(s || '').match(/[0-9][0-9.\-/ ]{3,}[0-9]/g) || [];

// Harvest normNum'd parcel-number CANDIDATES from a doc's extracted TEXT fields — so matching survives the AI
// routing the parcel/PCN into a different field each run (we've watched one bill land it in account_number,
// parcel_number, and neither). Tiered by reliability + false-positive surface:
//   • identifier fields (account_number, parcel_number): the value IS the number → keep len>=4 (short APNs like
//     NY "18.-2-53" → "18253" survive).
//   • legal_description: parcelNumFromLegal's targeted hit (>=4) + a general token scan (>=6).
//   • loose free text (notes, address): ONLY when includeLoose — free text can hold phone/case/date numbers, so
//     this is safe only under a caller that scopes candidates (step (b), one client's few parcels), NEVER
//     the global step-(a) scan. Tokens >=6 to drop street numbers.
// Returns [{ raw, norm }] deduped by norm.
export function candidateNumbers(ex, { includeLoose = false } = {}) {
  const byNorm = new Map();
  const add = (raw, minLen) => { const n = normNum(raw); if (n.length >= minLen && !byNorm.has(n)) byNorm.set(n, raw); };
  add(ex.account, 4);
  add(ex.parcel, 4);
  add(parcelNumFromLegal(ex.legal), 4);
  tokenizeNums(ex.legal).forEach(t => add(t, 6));
  if (includeLoose) { tokenizeNums(ex.notes).forEach(t => add(t, 6)); tokenizeNums(ex.address).forEach(t => add(t, 6)); }
  return [...byNorm].map(([norm, raw]) => ({ raw, norm }));
}

// Entity names: uppercase, drop punctuation, collapse whitespace, canonicalize spaced suffixes. coreEntity also
// strips the trailing legal-type suffix so "CPUS BESSEMER LP" matches a stored "CPUS Bessemer" / "CPUS Bessemer, LP".
const ENTITY_SUFFIX = /\s+(LP|LLP|LLC|INC|CORP|CORPORATION|GP|LTD|CO|COMPANY|TRUST)$/;
export const normEntity = (s) => String(s || '').toUpperCase().replace(/[.,]/g, ' ').replace(/\s+/g, ' ').trim()
  .replace(/\bL\s+L\s+C\b/g, 'LLC').replace(/\bL\s+L\s+P\b/g, 'LLP').replace(/\bL\s+P\b/g, 'LP');
export const coreEntity = (s) => normEntity(s).replace(ENTITY_SUFFIX, '').trim();

// Addresses: uppercase, strip punctuation, canonicalize common street-type suffixes so "2010 BROENING HWY" (extracted)
// matches a stored "2010 Broening Hwy." / "…Highway". Used to narrow a client's property list by a confident address.
const STREET_SUFFIX = [
  [/\b(HIGHWAY|HWY)\b/g, 'HWY'], [/\b(STREET|STR|ST)\b/g, 'ST'], [/\b(AVENUE|AVE|AV)\b/g, 'AVE'],
  [/\b(ROAD|RD)\b/g, 'RD'], [/\b(BOULEVARD|BLVD)\b/g, 'BLVD'], [/\b(DRIVE|DRV|DR)\b/g, 'DR'],
  [/\b(LANE|LN)\b/g, 'LN'], [/\b(COURT|CT)\b/g, 'CT'], [/\b(PARKWAY|PKWY)\b/g, 'PKWY'],
  [/\b(PLACE|PL)\b/g, 'PL'], [/\b(TERRACE|TER)\b/g, 'TER'], [/\b(CIRCLE|CIR)\b/g, 'CIR'],
  [/\b(SUITE|STE)\b/g, 'STE'], [/\b(NORTH|N)\b/g, 'N'], [/\b(SOUTH|S)\b/g, 'S'], [/\b(EAST|E)\b/g, 'E'], [/\b(WEST|W)\b/g, 'W'],
];
export const normAddr = (s) => {
  let a = String(s || '').toUpperCase().replace(/[.,#]/g, ' ').replace(/\s+/g, ' ').trim();
  for (const [re, to] of STREET_SUFFIX) a = a.replace(re, to);
  return a.replace(/\s+/g, ' ').trim();
};

async function propWithParcels(masterId) {
  const { data: props } = await supabase.from('properties').select('*').eq('master_property_id', masterId).limit(1);
  if (!props?.length) return null;
  const { data: kids } = await supabase.from('parcels').select('*').eq('master_property_id', masterId).order('parcel_name');
  return { property: props[0], parcels: kids || [] };
}

// Cascading match — use every identifier the doc gives us, in strength order, recording which signal(s) hit +
// confidence. Always keeps the extracted identifiers so the caller can surface them when nothing matches.
export async function cascadeMatch(x) {
  const ex = {
    owner: x.owner_name || '', address: x.property_address || '', account: x.account_number || '',
    parcel: x.parcel_number || '', collector: x.collector || '', legal: x.legal_description || '', state: x.state || '',
    mpid: x.master_property_id || x.mpid || '', notes: x.notes || '',
  };
  try {
    // a. parcel / assessor number → parcel (strongest). The bill's account_number is often the COLLECTOR's
    //    account; the real parcel number may be a dedicated field OR buried in the legal description. Match both,
    //    normalized, against parcels.account_number + sigertax_parcel_id (NOT assessor_ref_id — that's a uuid).
    // GLOBAL scan (all parcels) → conservative harvest: identifier fields + legal only (NO notes/address free
    // text, which could false-hit across 3,795 parcels). candidateNumbers defaults to includeLoose:false.
    const searchNums = candidateNumbers(ex);
    if (searchNums.length) {
      // ex.state comes RAW from a non-deterministic AI extraction; parcels.state is strict 2-letter uppercase.
      // Normalize first — a raw "Alabama"/"al" in an .eq('state', …) matches ZERO rows and vetoes an otherwise-good
      // parcel-number hit. If it doesn't resolve to a valid code, treat state as ABSENT (search unscoped) and warn.
      const stateCode = toStateCode(ex.state);
      if (ex.state && !stateCode) console.warn(`[cascade] unrecognized extracted state '${ex.state}' — searching unscoped`);
      const scanCands = async (useState) => {
        // PostgREST caps responses at 1000 rows regardless of .limit() — a bare .limit(4000) silently
        // returns only 1000, which truncates the UNSCOPED fallback (3,795 parcels) and drops any parcel
        // past the cut. That silently defeated this very fallback for a late-imported FL parcel whose
        // extracted state didn't scope. Paginate with .range() so the candidate set is actually complete.
        const out = [];
        for (let from = 0; ; from += 1000) {
          let q = supabase.from('parcels').select('id,account_number,sigertax_parcel_id,master_property_id,state');
          if (useState) q = q.eq('state', useState);
          const { data } = await q.range(from, from + 999);
          out.push(...(data || []));
          if (!data || data.length < 1000) break;
        }
        return out;
      };
      const findHit = (cands) => {
        for (const p of cands) {
          const pnums = [normNum(p.account_number), normNum(p.sigertax_parcel_id)].filter(Boolean);
          const m = searchNums.find(b => pnums.some(pn => pn === b.norm || (b.norm.length >= 6 && pn.includes(b.norm)) || (pn.length >= 6 && b.norm.includes(pn))));
          if (m) return { hit: p, matchedRaw: m.raw };
        }
        return null;
      };
      // Try state-scoped first (cheap when the state is right); on NO hit, retry UNSCOPED so a mis-extracted
      // state can never suppress a correct parcel-number match. Both scans paginate (scanCands), so neither is
      // capped at 1000 — do NOT "optimize" back to a single .limit(N>1000), which PostgREST silently truncates.
      let found = stateCode ? findHit(await scanCands(stateCode)) : null;
      if (!found) {
        if (stateCode) console.warn(`[cascade] no parcel under state '${stateCode}' — retrying unscoped`);
        found = findHit(await scanCands(null));
      }
      if (found) {
        const { hit, matchedRaw } = found;
        const pw = await propWithParcels(hit.master_property_id);
        // Surface the SPECIFIC parcel step (a) matched (via its number) so the consumer can preselect it —
        // propWithParcels returns ALL of the property's parcels, which otherwise forces a manual re-pick.
        if (pw) return { ...pw, matchedParcel: pw.parcels.find(p => p.id === hit.id) || null, matchedOn: [{ field: `Parcel number ${matchedRaw}`, confidence: 'high' }], extracted: ex };
      }
    }
    // a½. MPID → property (exact). master_property_id is Wigley's own primary property key — an EXACT numeric match,
    //     not fuzzy. Normalize first: strip everything but digits so "#65120" / "MPID 65120" / "prop-65120" → 65120
    //     (never trust a raw extracted value in an exact match). County docs (assessor notices, collector bills)
    //     won't carry an MPID — they show the county's numbers; this step fires for docs from our systems or client
    //     docs carrying our codes, and grows in value as MPID becomes the primary property identifier.
    const mpid = String(ex.mpid).replace(/\D/g, '');
    if (mpid.length >= 3) {
      const pw = await propWithParcels(Number(mpid));
      if (pw) return { ...pw, matchedOn: [{ field: `MPID ${mpid}`, confidence: 'high' }], extracted: ex };
    }
    // b. owner name → client legal entity names (normalized, suffix-agnostic) → the client's properties
    if (ex.owner && ex.owner.length > 2) {
      const target = coreEntity(ex.owner);
      if (target) {
        const { data: allCl } = await supabase.from('clients').select('id,name,legal_entity_names');
        const client = (allCl || []).find(cl => coreEntity(cl.name) === target
          || String(cl.legal_entity_names || '').split(',').some(e => { const ce = coreEntity(e); return ce && ce === target; }));
        if (client) {
          const { data: cProps } = await supabase.from('properties').select('*').eq('client_id', client.id).order('name');
          const props = (cProps || []).filter(p => !ex.state || !p.state || p.state === ex.state);
          const label = `Owner → ${client.name} (legal entity)`;
          if (props.length === 1) {
            const { data: kids } = await supabase.from('parcels').select('*').eq('master_property_id', props[0].master_property_id).order('parcel_name');
            return { property: props[0], parcels: kids || [], client, matchedOn: [{ field: label, confidence: 'medium' }], extracted: ex };
          }
          if (props.length > 1) {
            // Client resolved but not the property. DON'T punt yet — narrow this client's (small) property list by the
            // extracted address first; scoped to one client, a fuzzy address compare is safe. A confident address that
            // uniquely matches auto-selects the property instead of dumping the whole client list on the user.
            let narrowed = props;
            const needle = normAddr(ex.address);
            if (needle.length >= 6) {
              const hits = props.filter(p => normAddr(`${p.address || ''} ${p.name || ''}`).includes(needle));
              if (hits.length) narrowed = hits;
            }
            // Still ambiguous → try the parcel/account number against the candidates' parcels (one scoped query).
            if (narrowed.length > 1) {
              // CLIENT-SCOPED (this client's few parcels) → aggressive harvest incl. notes/address free text:
              // the scope contains any stray false hit, so we chase the PCN wherever the AI routed it this run.
              const nums = candidateNumbers(ex, { includeLoose: true }).map(c => c.norm);
              if (nums.length) {
                const ids = narrowed.map(p => p.master_property_id);
                // Paginate — a client with >1000 parcels would silently truncate here (1000-row cap) and
                // drop the parcel carrying the match number, punting an otherwise-resolvable doc to manual.
                const kids = [];
                for (let from = 0; ; from += 1000) {
                  const { data } = await supabase.from('parcels').select('master_property_id,account_number,sigertax_parcel_id').in('master_property_id', ids).range(from, from + 999);
                  kids.push(...(data || []));
                  if (!data || data.length < 1000) break;
                }
                const hitMpids = new Set((kids || []).filter(k => {
                  const pn = [normNum(k.account_number), normNum(k.sigertax_parcel_id)].filter(Boolean);
                  return nums.some(b => pn.some(x => x === b || (b.length >= 6 && x.includes(b)) || (x.length >= 6 && b.includes(x))));
                }).map(k => k.master_property_id));
                const byNum = narrowed.filter(p => hitMpids.has(p.master_property_id));
                if (byNum.length) narrowed = byNum;
              }
            }
            if (narrowed.length === 1) {
              const { data: kids } = await supabase.from('parcels').select('*').eq('master_property_id', narrowed[0].master_property_id).order('parcel_name');
              return { property: narrowed[0], parcels: kids || [], client, matchedOn: [{ field: `${label} + address`, confidence: 'medium' }], extracted: ex };
            }
            return { property: null, parcels: [], client, candidates: narrowed, matchedOn: [{ field: label, confidence: 'medium' }], extracted: ex };
          }
        }
      }
    }
    // c. property address → parcel
    if (ex.address && ex.address.length > 5) {
      const addrQ = ex.address.replace(/[^a-zA-Z0-9\s]/g, '').trim().split(/\s+/).slice(0, 3).join(' ');
      const { data: hits } = await supabase.from('parcels').select('*').ilike('address', `%${addrQ}%`).limit(5);
      if (hits?.length) { const pw = await propWithParcels(hits[0].master_property_id); if (pw) return { ...pw, matchedOn: [{ field: 'Property Address', confidence: 'medium' }], extracted: ex }; }
    }
    // d. legal description → parcel
    if (ex.legal && ex.legal.length > 8) {
      const legalQ = ex.legal.replace(/[^a-zA-Z0-9\s]/g, '').trim().split(/\s+/).slice(0, 4).join(' ');
      const { data: hits } = await supabase.from('parcels').select('*').ilike('legal_description', `%${legalQ}%`).limit(5);
      if (hits?.length) { const pw = await propWithParcels(hits[0].master_property_id); if (pw) return { ...pw, matchedOn: [{ field: 'Legal Description', confidence: 'medium' }], extracted: ex }; }
    }
  } catch (e) { console.error('[cascade match]', e); }
  // e. nothing matched — surface what the doc said so the caller can search from it
  return { property: null, parcels: [], unmatched: true, extracted: ex };
}
